import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Edit3, Trash2, MoreHorizontal, Sparkles, MessageSquarePlus } from "lucide-react";

// Confirmation Card for actions - Enhanced with animations
export function ConfirmationCard({ 
  type = "success",
  title,
  details = [],
  onEdit,
  onDismiss,
  show = true
}) {
  const typeStyles = {
    success: {
      icon: Check,
      iconBg: "bg-brand-green/10",
      iconColor: "text-brand-green",
      border: "border-brand-green/30",
      glow: "shadow-lg shadow-brand-green/10",
    },
    info: {
      icon: Sparkles,
      iconBg: "bg-brand-cyan/10",
      iconColor: "text-brand-cyan",
      border: "border-brand-cyan/30",
      glow: "shadow-lg shadow-brand-cyan/10",
    },
    warning: {
      icon: MoreHorizontal,
      iconBg: "bg-status-warning/10",
      iconColor: "text-status-warning",
      border: "border-status-warning/30",
      glow: "shadow-lg shadow-status-warning/10",
    },
    error: {
      icon: X,
      iconBg: "bg-status-error/10",
      iconColor: "text-status-error",
      border: "border-status-error/30",
      glow: "shadow-lg shadow-status-error/10",
    },
  };

  const style = typeStyles[type] || typeStyles.success;
  const Icon = style.icon;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, x: 30, scale: 0.9 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, x: 30, scale: 0.9 }}
          transition={{ 
            duration: 0.4, 
            type: "spring",
            stiffness: 300,
            damping: 25
          }}
          className={`bg-nectar-card border ${style.border} ${style.glow} rounded-xl p-4 mb-4 backdrop-blur-sm`}
          data-testid="confirmation-card"
        >
          <div className="flex items-start gap-3">
            <motion.div 
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.1, type: "spring", stiffness: 500 }}
              className={`w-8 h-8 rounded-lg ${style.iconBg} flex items-center justify-center flex-shrink-0`}
            >
              <Icon className={`w-4 h-4 ${style.iconColor}`} />
            </motion.div>
            
            <div className="flex-1 min-w-0">
              <motion.h4 
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="text-white font-medium text-sm mb-2"
              >
                {title}
              </motion.h4>
              
              {details.length > 0 && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="space-y-1 mb-3"
                >
                  {details.map((detail, idx) => (
                    <motion.div 
                      key={idx} 
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.25 + idx * 0.05 }}
                      className="flex items-center gap-2 text-xs"
                    >
                      <span className="text-slate-500">{detail.label}:</span>
                      <span className="text-slate-300 truncate">{detail.value}</span>
                    </motion.div>
                  ))}
                </motion.div>
              )}
              
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="flex items-center gap-2"
              >
                {onEdit && (
                  <button
                    onClick={onEdit}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white text-xs transition-all duration-200 hover:scale-105 active:scale-95"
                  >
                    <Edit3 className="w-3 h-3" />
                    <span>Editar</span>
                  </button>
                )}
                {onDismiss && (
                  <button
                    onClick={onDismiss}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-white/5 text-slate-500 hover:text-slate-300 text-xs transition-all duration-200"
                  >
                    <X className="w-3 h-3" />
                    <span>Fechar</span>
                  </button>
                )}
              </motion.div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Message Bubble Component
export function MessageBubble({ message, isUser = false }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex ${isUser ? "justify-end" : "justify-start"} mb-3`}
    >
      <div
        className={`
          max-w-[85%] px-4 py-3 rounded-2xl text-sm
          ${isUser 
            ? "bg-brand-cyan/10 border border-brand-cyan/30 text-white rounded-br-md" 
            : "bg-nectar-card border border-nectar-border text-slate-300 rounded-bl-md"
          }
        `}
      >
        {message}
      </div>
    </motion.div>
  );
}

// Context Panel Component
//
// Mobile Parity Epic — Etapa 2: nova prop variant.
//   - "permanent" (default): aside fixed direita, comportamento legado desktop.
//   - "bottom-sheet": overlay full-screen mobile, slide-up.
// Slot opcional `chatInput` (fora do scroll, fixado no rodapé) — preenchido
// na Etapa 4 quando o chat input migrar pra cá.
export default function ContextPanel({
  messages = [],
  confirmations = [],
  isOpen = true,
  onClose,
  onNewChat,
  children,
  variant = "permanent",
  chatInput = null,
}) {
  // Mobile Parity Hotfix Issue 1: scroll automático pro fim do container
  // ao abrir o painel (mobile sheet OU desktop sidebar) e ao chegar nova
  // mensagem. requestAnimationFrame espera o AnimatePresence montar o
  // DOM completo antes de medir scrollHeight.
  const scrollContainerRef = useRef(null);
  useEffect(() => {
    if (!isOpen || !scrollContainerRef.current) return;
    requestAnimationFrame(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
      }
    });
  }, [isOpen, messages?.length]);

  // === BOTTOM SHEET MODE (mobile) ===
  if (variant === "bottom-sheet") {
    return (
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={onClose}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
              data-testid="context-panel-backdrop"
            />
            <motion.section
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ duration: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
              className="fixed inset-0 z-50 bg-nectar-bg flex flex-col safe-area-pt"
              data-testid="context-panel-sheet"
            >
              {/* Header */}
              <header className="h-12 flex items-center justify-between px-4 border-b border-nectar-border shrink-0">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-brand-cyan" />
                  <span className="text-sm font-medium text-white">Painel Inteligente</span>
                </div>
                <div className="flex items-center gap-1">
                  {onNewChat && messages.length > 0 && (
                    <button
                      type="button"
                      onClick={onNewChat}
                      aria-label="Nova conversa"
                      className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white transition-colors"
                      data-testid="new-chat-btn-mobile"
                    >
                      <MessageSquarePlus className="w-4 h-4" />
                    </button>
                  )}
                  {onClose && (
                    <button
                      type="button"
                      onClick={onClose}
                      aria-label="Fechar"
                      className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white transition-colors"
                      data-testid="context-panel-close"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </header>

              {/* Content scrollável. Hotfix: ref pra auto-scroll. */}
              <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-4">
                {confirmations.map((conf, idx) => (
                  <ConfirmationCard key={idx} {...conf} />
                ))}
                {children}
              </div>

              {/* Slot do chat input (Etapa 4 — preenche; até lá, fica vazio
                  com safe-area-pb pra clearance do home indicator iOS). */}
              <div className="shrink-0 border-t border-nectar-border safe-area-pb">
                {chatInput}
              </div>
            </motion.section>
          </>
        )}
      </AnimatePresence>
    );
  }

  // === PERMANENT MODE (desktop) — código original intacto ===
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.aside
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 20 }}
          transition={{ duration: 0.3 }}
          className="w-80 lg:w-96 h-full bg-nectar-bg border-l border-nectar-border flex flex-col"
          data-testid="context-panel"
        >
          {/* Header */}
          <div className="h-16 px-4 flex items-center justify-between border-b border-nectar-border">
            <h3 className="font-display font-semibold text-white tracking-wide">Painel Inteligente</h3>
            <div className="flex items-center gap-1">
              {onNewChat && messages.length > 0 && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  onClick={onNewChat}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-brand-cyan/10 hover:bg-brand-cyan/20 text-brand-cyan text-xs font-medium transition-all duration-200 hover:scale-105 active:scale-95"
                  data-testid="new-chat-btn"
                  title="Novo chat"
                >
                  <MessageSquarePlus className="w-3.5 h-3.5" />
                  <span>Novo</span>
                </motion.button>
              )}
              {onClose && (
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Content. Hotfix: ref pra auto-scroll na última mensagem. */}
          <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-4">
            {/* Confirmation Cards */}
            {confirmations.map((conf, idx) => (
              <ConfirmationCard key={idx} {...conf} />
            ))}

            {/* Custom Children (includes messages with DataCards) */}
            {children}
          </div>

          {/* Slot do chat input (Etapa 4) — visível em desktop também. */}
          {chatInput && (
            <div className="shrink-0 border-t border-nectar-border">
              {chatInput}
            </div>
          )}
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
